/**
 * T9 of the optimization program: bounded concurrency for independent
 * requests. See `docs/2026-09-03-plan-optimizacion-requests-renders.md`.
 *
 * The browser opens at most six HTTP/1.1 connections per host, so firing N
 * requests at once past that only queues them inside the browser, while
 * awaiting them one by one pays N round-trip latencies. A pool of workers
 * pulling from a shared cursor keeps `concurrency` requests in flight and
 * leaves a socket free for whatever else the screen needs.
 *
 * Not p-limit: the copy in node_modules is a transitive dependency of jest's
 * find-up, not declared in package.json, and every later major is ESM-only,
 * which CRA 5's Jest does not transform.
 */

/** Five, not six: the sixth socket stays free for unrelated requests. */
export const DEFAULT_CONCURRENCY = 5;

/**
 * Like Promise.all(items.map(fn)) but never more than `concurrency` calls in
 * flight. Results come back in input order, whichever order the calls finish
 * in, which is what the callers rely on to keep a response aligned with the
 * item that produced it. A rejected call rejects the whole map, as Promise.all
 * does; most services in this repo resolve with null or an error envelope
 * instead of rejecting.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  fn: (item: T, index: number) => Promise<R>,
  concurrency: number = DEFAULT_CONCURRENCY
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;

  const worker = async (): Promise<void> => {
    while (next < items.length) {
      // No await between reading and incrementing the cursor, so two workers
      // never take the same index.
      const index = next++;
      results[index] = await fn(items[index], index);
    }
  };

  const workerCount = Math.max(1, Math.min(concurrency, items.length));
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

/** Drops repeated items, keeping the first occurrence's position. */
export const unique = <T,>(items: readonly T[]): T[] => Array.from(new Set(items));
